<script setup>
import { computed, ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import { AlertCircle, ChevronDown, HardDrive, Clock, Maximize2, Film, FileType, Info } from 'lucide-vue-next';
import { UPLOAD_LIMITS } from '@/constants/uploadLimits';
import DSAlert from './DSAlert.vue';

const props = defineProps({
    mediaType: {
        type: String,
        required: true,
        validator: (v) => ['video', 'image'].includes(v),
    },
    violations: {
        type: Array,
        default: () => [],
    },
    // When true and there are no violations, render the limits collapsed under a toggle.
    collapsible: {
        type: Boolean,
        default: false,
    },
});

const { t } = useTranslation();
const tk = (key, params) => t(`designSystem.uploadLimitsCard.${key}`, params);

const hasViolations = computed(() => props.violations.length > 0);
const expanded = ref(!props.collapsible || hasViolations.value);

const formatMinutes = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m === 0) return tk('value.seconds', { n: s });
    if (s === 0) return tk('value.minutes', { n: m });
    return tk('value.minutesSeconds', { m, s });
};

const limits = computed(() => {
    const isVideo = props.mediaType === 'video';
    const rows = [];

    rows.push({
        key: 'size',
        icon: HardDrive,
        label: tk('row.size'),
        value: tk('value.maxMb', { n: isVideo ? UPLOAD_LIMITS.MAX_VIDEO_SIZE_MB : UPLOAD_LIMITS.MAX_IMAGE_SIZE_MB }),
    });

    if (isVideo) {
        rows.push({
            key: 'duration',
            icon: Clock,
            label: tk('row.duration'),
            value: tk('value.range', {
                min: tk('value.seconds', { n: UPLOAD_LIMITS.MIN_VIDEO_DURATION_SEC }),
                max: formatMinutes(UPLOAD_LIMITS.MAX_VIDEO_DURATION_SEC),
            }),
        });
    }

    rows.push({
        key: 'resolution',
        icon: Maximize2,
        label: tk('row.resolution'),
        value: tk('value.range', {
            min: `${UPLOAD_LIMITS.MIN_RESOLUTION_PX}px`,
            max: `${UPLOAD_LIMITS.MAX_RESOLUTION_PX}px (4K)`,
        }),
    });

    if (isVideo) {
        rows.push({
            key: 'fps',
            icon: Film,
            label: tk('row.fps'),
            value: tk('value.range', {
                min: `${UPLOAD_LIMITS.MIN_VIDEO_FPS}fps`,
                max: `${UPLOAD_LIMITS.MAX_VIDEO_FPS}fps`,
            }),
        });
    }

    rows.push({
        key: 'formats',
        icon: FileType,
        label: tk('row.formats'),
        value: (isVideo ? UPLOAD_LIMITS.ACCEPTED_VIDEO_FORMATS : UPLOAD_LIMITS.ACCEPTED_IMAGE_FORMATS).join(', '),
    });

    return rows;
});

const toggle = () => { expanded.value = !expanded.value; };
</script>

<template>
    <div class="upload-limits-card">
        <DSAlert v-if="hasViolations" variant="error" :title="tk('violations.title')">
            <template #icon><AlertCircle :size="20" /></template>
            <ul class="upload-limits-card__violations">
                <li v-for="(v, i) in violations" :key="i">{{ v }}</li>
            </ul>
        </DSAlert>

        <div class="upload-limits-card__panel" :class="{ 'upload-limits-card__panel--error': hasViolations }">
            <button
                v-if="collapsible && !hasViolations"
                type="button"
                class="upload-limits-card__toggle"
                :aria-expanded="expanded"
                @click="toggle"
            >
                <Info :size="16" class="upload-limits-card__toggle-icon" />
                <span class="upload-limits-card__toggle-label">{{ tk('limits.title', { type: tk(`mediaType.${mediaType}`) }) }}</span>
                <ChevronDown :size="16" :class="['upload-limits-card__chevron', { 'upload-limits-card__chevron--up': expanded }]" />
            </button>

            <div v-else class="upload-limits-card__header">
                <Info :size="16" class="upload-limits-card__header-icon" />
                <span class="upload-limits-card__header-label">{{ tk('limits.title', { type: tk(`mediaType.${mediaType}`) }) }}</span>
            </div>

            <ul v-if="expanded" class="upload-limits-card__list">
                <li v-for="row in limits" :key="row.key" class="upload-limits-card__row">
                    <component :is="row.icon" :size="16" class="upload-limits-card__row-icon" />
                    <span class="upload-limits-card__row-label">{{ row.label }}</span>
                    <span class="upload-limits-card__row-value">{{ row.value }}</span>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
.upload-limits-card {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    font-family: "Noto Sans", sans-serif;
}

.upload-limits-card__violations {
    margin: 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.upload-limits-card__violations li {
    font-size: var(--font-size-body-small);
    line-height: 1.5;
    color: var(--color-error-dark, var(--color-error));
}

.upload-limits-card__panel {
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    background: var(--color-background-light);
    overflow: hidden;
}

.upload-limits-card__panel--error {
    border-color: var(--color-border-default);
}

.upload-limits-card__toggle,
.upload-limits-card__header {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    width: 100%;
    padding: 10px 14px;
    background: transparent;
    border: none;
    text-align: left;
    cursor: default;
    color: var(--color-text-secondary);
}

.upload-limits-card__toggle {
    cursor: pointer;
    transition: var(--transition-default);
}

.upload-limits-card__toggle:hover {
    background: var(--color-tertiary, var(--color-background-light));
}

.upload-limits-card__toggle-icon,
.upload-limits-card__header-icon {
    flex-shrink: 0;
    color: var(--color-text-tertiary);
}

.upload-limits-card__toggle-label,
.upload-limits-card__header-label {
    flex: 1;
    font-size: var(--font-size-label, 13px);
    font-weight: 600;
    letter-spacing: 0.01em;
    color: var(--color-text-primary);
}

.upload-limits-card__chevron {
    color: var(--color-text-tertiary);
    transition: transform 0.18s ease;
}

.upload-limits-card__chevron--up {
    transform: rotate(180deg);
}

.upload-limits-card__list {
    list-style: none;
    margin: 0;
    padding: 4px 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    border-top: 1px solid var(--color-border-subtle);
}

.upload-limits-card__row {
    display: grid;
    grid-template-columns: 18px 1fr auto;
    align-items: center;
    gap: var(--spacing-sm);
    padding: 6px 0;
    font-size: 13px;
}

.upload-limits-card__row + .upload-limits-card__row {
    border-top: 1px dashed var(--color-border-subtle);
}

.upload-limits-card__row-icon {
    color: var(--color-primary);
    opacity: 0.85;
}

.upload-limits-card__row-label {
    color: var(--color-text-secondary);
}

.upload-limits-card__row-value {
    font-weight: 600;
    color: var(--color-text-primary);
    font-variant-numeric: tabular-nums;
}
</style>
